import React, { useCallback } from 'react';

import { useFlags } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/core/check-mark';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useKnowledgeSourcesActions } from '@atlassian/conversation-assistant-store/controllers/connectors-store';

import type { OAuthHandlers } from './OAuthHandlers';

interface ConnectorOAuthData {
	friendlyName: string;
	appSource: string;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onFailAction?: () => void;
	analyticsAttributes: {
		source: string;
		isNewlyEnabled?: boolean;
		surface?: string;
	};
}

/**
 * Returns a factory function that creates OAuth handlers for a given connector.
 * Call the hooks once, then use the returned factory to produce per-connector
 * handlers on the fly — useful when the connector is not known at hook-call time.
 */
export const useOAuthHandlerFactory = (): ((connector: ConnectorOAuthData) => OAuthHandlers) => {
	const { showFlag } = useFlags();
	const { sendAnalyticsEvent } = useAnalytics();
	const { markUserConnected } = useKnowledgeSourcesActions();

	return useCallback(
		({
			friendlyName,
			appSource,
			outboundAuthUrl,
			onCompletedAction = () => {},
			onFailAction = () => {},
			analyticsAttributes: { source, isNewlyEnabled, surface },
		}: ConnectorOAuthData): OAuthHandlers => {
			const onSuccess = (): void => {
				showFlag({
					title: `Connected to ${friendlyName}`,
					icon: <SuccessIcon spacing="spacious" label="" color={token('color.icon.success')} />,
					isAutoDismiss: true,
				});

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'completed',
					actionSubject: 'thirdPartyOAuth',
					attributes: {
						productSelected: appSource,
						source,
						isNewlyEnabled,
						surface,
					},
				});

				markUserConnected(appSource);

				onCompletedAction();
			};

			const onFail = (): void => {
				showFlag({
					title: `Failed to connect to ${friendlyName}`,
					icon: <ErrorIcon spacing="spacious" label="" color={token('color.icon.danger')} />,
					isAutoDismiss: true,
				});

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: 'thirdPartyOAuth',
					attributes: {
						productSelected: appSource,
						source,
						isNewlyEnabled,
						surface,
					},
				});

				onFailAction();
			};

			const onAuthorize = async (): Promise<void> => {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'clicked',
					actionSubject: 'thirdPartyOAuth',
					attributes: {
						productSelected: appSource,
						source,
						isNewlyEnabled,
						surface,
					},
				});

				try {
					await auth(outboundAuthUrl);
					onSuccess();
				} catch {
					onFail();
				}
			};

			return { onAuthorize, onSuccess, onFail };
		},
		[showFlag, sendAnalyticsEvent, markUserConnected],
	);
};
